import type { AspectRatioKey } from '@/scenes/Editor/components/FloatingInspectorButton/SharedComponents/Popover/ratioOptions';
import {
  PHOTOSHOOT_ACCEPTED_IMAGE_TYPES,
  PHOTOSHOOT_MAX_BATCH_BYTES,
  PHOTOSHOOT_MAX_BLOB_BYTES,
  PHOTOSHOOT_RESULT_COUNT,
  VALID_PHOTOSHOOT_ASPECT_RATIOS,
} from '@/utils/indexedPhotoshoot';

export const PHOTOSHOOT_HANDOFF_ROUTE_PATH = '/photoshoot-handoff';
export const PHOTOSHOOT_HANDOFF_PROTOCOL_VERSION = 'photoshoot-handoff/v1';

export const HANDOFF_READY = 'HANDOFF_READY';
export const HANDOFF_PAYLOAD = 'HANDOFF_PAYLOAD';
export const HANDOFF_STORED = 'HANDOFF_STORED';
export const HANDOFF_ERROR = 'HANDOFF_ERROR';

export const DEFAULT_HANDOFF_TIMEOUT_MS = 30_000;
export const MIN_HANDOFF_TIMEOUT_MS = 5_000;
export const MAX_HANDOFF_TIMEOUT_MS = 120_000;
export const HANDOFF_TIMEOUT_QUERY_PARAM = 'handoffTimeoutMs';
export const HANDOFF_SENDER_ORIGIN_QUERY_PARAM = 'senderOrigin';

export interface PhotoshootHandoffPayload {
  requestId: string;
  transferId: string;
  sourceBlob: Blob;
  resultBlobs: Blob[];
  labels?: string[];
  aspectRatio?: AspectRatioKey;
  ssoCode?: string;
}

export interface HandoffReadyMessage {
  type: typeof HANDOFF_READY;
  protocol: typeof PHOTOSHOOT_HANDOFF_PROTOCOL_VERSION;
  timeoutMs: number;
}

export interface HandoffStoredMessage {
  type: typeof HANDOFF_STORED;
  protocol: typeof PHOTOSHOOT_HANDOFF_PROTOCOL_VERSION;
  requestId: string;
  transferId: string;
}

export interface HandoffErrorMessage {
  type: typeof HANDOFF_ERROR;
  protocol: typeof PHOTOSHOOT_HANDOFF_PROTOCOL_VERSION;
  code: string;
  message: string;
  requestId?: string;
}

type ValidatePayloadResult =
  | { ok: true; payload: PhotoshootHandoffPayload }
  | { ok: false; error: string; requestId?: string };

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function isNonEmptyString(value: unknown): value is string {
  return typeof value === 'string' && value.trim().length > 0;
}

function isAllowedBlob(blob: unknown): blob is Blob {
  if (!isRecord(blob)) return false;
  const maybeBlob = blob as {
    size?: unknown;
    type?: unknown;
    arrayBuffer?: unknown;
  };
  if (
    typeof maybeBlob.size !== 'number' ||
    typeof maybeBlob.type !== 'string' ||
    typeof maybeBlob.arrayBuffer !== 'function'
  ) {
    return false;
  }

  return (
    maybeBlob.size > 0 &&
    maybeBlob.size <= PHOTOSHOOT_MAX_BLOB_BYTES &&
    PHOTOSHOOT_ACCEPTED_IMAGE_TYPES.includes(
      maybeBlob.type as (typeof PHOTOSHOOT_ACCEPTED_IMAGE_TYPES)[number],
    )
  );
}

function toSafeErrorMessage(error: unknown): string {
  return error instanceof Error ? error.message : 'unknown';
}

export function clampHandoffTimeoutMs(value: unknown): number {
  const parsed = typeof value === 'string' ? Number.parseInt(value, 10) : Number(value);
  if (!Number.isFinite(parsed)) return DEFAULT_HANDOFF_TIMEOUT_MS;
  return Math.max(MIN_HANDOFF_TIMEOUT_MS, Math.min(MAX_HANDOFF_TIMEOUT_MS, parsed));
}

export function getHandoffTimeoutFromSearch(search: string): number {
  const params = new URLSearchParams(search);
  return clampHandoffTimeoutMs(params.get(HANDOFF_TIMEOUT_QUERY_PARAM));
}

export function getSenderOriginFromSearch(search: string): string | null {
  const params = new URLSearchParams(search);
  const senderOrigin = params.get(HANDOFF_SENDER_ORIGIN_QUERY_PARAM);
  if (!senderOrigin) return null;
  try {
    const origin = new URL(senderOrigin).origin;
    return isTrustedBazaartOrigin(origin) ? origin : null;
  } catch {
    return null;
  }
}

export function getTrustedReferrerOrigin(referrer: string): string | null {
  if (!isNonEmptyString(referrer)) return null;
  try {
    const origin = new URL(referrer).origin;
    return isTrustedBazaartOrigin(origin) ? origin : null;
  } catch {
    return null;
  }
}

export function isTrustedBazaartOrigin(origin: string): boolean {
  try {
    const host = new URL(origin).hostname;
    return host === 'bazaart.com' || host.endsWith('.bazaart.com');
  } catch {
    return false;
  }
}

export function createHandoffReadyMessage(timeoutMs: number): HandoffReadyMessage {
  return {
    type: HANDOFF_READY,
    protocol: PHOTOSHOOT_HANDOFF_PROTOCOL_VERSION,
    timeoutMs: clampHandoffTimeoutMs(timeoutMs),
  };
}

export function createHandoffStoredMessage(payload: {
  requestId: string;
  transferId: string;
}): HandoffStoredMessage {
  return {
    type: HANDOFF_STORED,
    protocol: PHOTOSHOOT_HANDOFF_PROTOCOL_VERSION,
    requestId: payload.requestId,
    transferId: payload.transferId,
  };
}

export function createHandoffErrorMessage(payload: {
  code: string;
  message: string;
  requestId?: string;
}): HandoffErrorMessage {
  return {
    type: HANDOFF_ERROR,
    protocol: PHOTOSHOOT_HANDOFF_PROTOCOL_VERSION,
    code: payload.code,
    message: payload.message,
    requestId: payload.requestId,
  };
}

export function validateHandoffPayloadMessage(value: unknown): ValidatePayloadResult {
  if (!isRecord(value)) {
    return { ok: false, error: 'Payload must be an object' };
  }
  if (value.type !== HANDOFF_PAYLOAD) {
    return { ok: false, error: 'Unexpected message type' };
  }
  const requestId = isNonEmptyString(value.requestId) ? value.requestId : undefined;
  if (!requestId) {
    return { ok: false, error: 'requestId is required' };
  }
  if (!isNonEmptyString(value.transferId)) {
    return { ok: false, error: 'transferId is required', requestId };
  }
  if (!isAllowedBlob(value.sourceBlob)) {
    return { ok: false, error: 'sourceBlob is invalid', requestId };
  }
  if (!Array.isArray(value.resultBlobs)) {
    return {
      ok: false,
      error: `resultBlobs must be empty or contain exactly ${PHOTOSHOOT_RESULT_COUNT} items`,
      requestId,
    };
  }
  const resultBlobs = value.resultBlobs;
  const resultBlobsCount = resultBlobs.length;
  if (resultBlobsCount !== 0 && resultBlobsCount !== PHOTOSHOOT_RESULT_COUNT) {
    return {
      ok: false,
      error: `resultBlobs must be empty or contain exactly ${PHOTOSHOOT_RESULT_COUNT} items`,
      requestId,
    };
  }
  if (!resultBlobs.every(isAllowedBlob)) {
    return { ok: false, error: 'resultBlobs contain invalid image data', requestId };
  }
  const totalSize =
    value.sourceBlob.size + resultBlobs.reduce((sum, blob) => sum + blob.size, 0);
  if (totalSize > PHOTOSHOOT_MAX_BATCH_BYTES) {
    return { ok: false, error: 'Batch exceeds size limit', requestId };
  }
  const labelsCandidate = value.labels;
  if (labelsCandidate != null) {
    if (
      !Array.isArray(labelsCandidate) ||
      labelsCandidate.length !== PHOTOSHOOT_RESULT_COUNT ||
      labelsCandidate.some(label => !isNonEmptyString(label))
    ) {
      return { ok: false, error: 'labels must contain exactly 4 non-empty strings', requestId };
    }
  }
  const aspectRatioCandidate = value.aspectRatio;
  if (
    aspectRatioCandidate != null &&
    !VALID_PHOTOSHOOT_ASPECT_RATIOS.has(aspectRatioCandidate as AspectRatioKey)
  ) {
    return { ok: false, error: 'aspectRatio is invalid', requestId };
  }
  const ssoCodeCandidate = value.ssoCode;
  if (ssoCodeCandidate != null && !isNonEmptyString(ssoCodeCandidate)) {
    return { ok: false, error: 'ssoCode must be a non-empty string when provided', requestId };
  }

  return {
    ok: true,
    payload: {
      requestId,
      transferId: value.transferId,
      sourceBlob: value.sourceBlob,
      resultBlobs: resultBlobs as Blob[],
      labels: labelsCandidate as string[] | undefined,
      aspectRatio: aspectRatioCandidate as AspectRatioKey | undefined,
      ssoCode: ssoCodeCandidate as string | undefined,
    },
  };
}

export function formatHandoffValidationError(error: unknown): string {
  return toSafeErrorMessage(error);
}
